import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@monksflow/monks-ui';
import { IdeationWidgetKey } from '@inference.engine/widgets/web';
import { getSavedIdeationSnapshot } from '@/features/ideation-widget';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { ArtifactPreviewFallback } from './ArtifactPreviewFallback';
import { ArtifactPreviewWidget } from './ArtifactPreviewWidget';

interface Props {
  artifactId: string;
  /** Which saved kind this is — decides whether the widget opens on the idea detail. */
  variant: 'campaign' | 'idea';
}

/** Library preview for a saved ideation artifact (campaign or single idea). */
export function IdeationArtifactPreview({ artifactId, variant }: Props) {
  const workspaceId = useCurrentWorkspaceId();

  if (!workspaceId) {
    return <ArtifactPreviewFallback />;
  }

  return (
    <ResolvedIdeationPreview workspaceId={workspaceId} artifactId={artifactId} variant={variant} />
  );
}

function ResolvedIdeationPreview({
  workspaceId,
  artifactId,
  variant,
}: {
  workspaceId: string;
  artifactId: string;
  variant: 'campaign' | 'idea';
}) {
  const query = useQuery({
    queryKey: ['ideation-artifact-snapshot', workspaceId, artifactId],
    queryFn: () => getSavedIdeationSnapshot(workspaceId, artifactId),
  });

  if (query.isPending) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Spinner />
      </div>
    );
  }

  if (query.isError) {
    return <ArtifactPreviewFallback />;
  }

  return (
    <ArtifactPreviewWidget
      artifactKey={IdeationWidgetKey}
      data={query.data as unknown as Record<string, unknown>}
      widgetProps={{ previewInitialIdeaIndex: variant === 'idea' ? 0 : null }}
      fallback={<ArtifactPreviewFallback />}
    />
  );
}
